/* eslint-disable @typescript-eslint/prefer-destructuring -- obj desc not possible */
// src/store/slices/auth/authSlice.ts
/* eslint-disable no-magic-numbers -- no magic number */
/* eslint-disable @typescript-eslint/no-magic-numbers -- nomagic number */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  type FieldErrors,
  initialAuthState,
  type LoginStep,
  type RegistrationData,
  type RegistrationStep,
} from '@/types/authState.types';
import { loginThunk } from '@/store/thunks/auth/loginThunk';
import { logoutThunk } from '@/store/thunks/auth/logoutThunk';
import { verifyOtpThunk } from '@/store/thunks/auth/verifyOtpThunk';
import { setPasswordThunk } from '@/store/thunks/auth/setPasswordThunk';
import { confirmEmailThunk } from '@/store/thunks/auth/confirmEmailThunk';
import { resendEmailCodeThunk } from '@/store/thunks/auth/resendEmailCodeThunk';
import { confirmPhoneThunk } from '@/store/thunks/auth/confirmPhoneThunk';
import { resendPhoneCodeThunk } from '@/store/thunks/auth/resendPhoneCodeThunk';
import { select2FAMethodThunk } from '@/store/thunks/auth/select2FAMethodThunk';
import { verify2faCodeThunk } from '@/store/thunks/auth/verify2faCodeThunk';
import { resend2faCodeThunk } from '@/store/thunks/auth/resend2faCodeThunk';
import { getErrorsFromPayload, isFieldErrors } from '@/store/utils/index';
import { fetchAdminInfoThunk } from '@/store/thunks/auth/fetchAdminInfoThunk';
import { sendEmailCodeThunk } from '@/store/thunks/auth/sendEmailCodeThunk';
import { forgotPasswordThunk } from '@/store/thunks/auth/forgotPasswordThunk';
import { verifyRecoveryTwoFactorThunk } from '@/store/thunks/auth/verifyRecoveryTwoFactorThunk';
import { sendRecoveryTwoFactorThunk } from '@/store/thunks/auth/sendRecoveryTwoFactorThunk';
import { verifyRecoveryOtpThunk } from '@/store/thunks/auth/verifyRecoveryOtpThunk';

const REGISTRATION_STEPS: RegistrationStep[] = [
  'otp',
  'emailCode',
  'phoneCode',
  'password',
  'complete',
];

export const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    clearLoginError(state) {
      state.error = undefined;
    },
    clearFieldErrors(state) {
      state.fieldErrors = {};
    },
    setLoginStep(state, action: PayloadAction<LoginStep>) {
      const { payload } = action;
      state.loginStep = payload;
    },
    registrationNextStep(state) {
      const idx = REGISTRATION_STEPS.indexOf(state.registration.step);
      const nextStep = REGISTRATION_STEPS[Math.min(idx + 1, REGISTRATION_STEPS.length - 1)];
      state.registration.step = nextStep;
    },
    registrationPrevStep(state) {
      const idx = REGISTRATION_STEPS.indexOf(state.registration.step);
      const prevStep = REGISTRATION_STEPS[Math.max(idx - 1, 0)];
      state.registration.step = prevStep;
    },
    registrationSetStep(state, action: PayloadAction<RegistrationStep>) {
      const { payload } = action;
      state.registration.step = payload;
    },
    registrationSetData(state, action: PayloadAction<Partial<RegistrationData>>) {
      state.registration.data = { ...state.registration.data, ...action.payload };
    },
    registrationSetLoading(state, action: PayloadAction<boolean>) {
      const { payload } = action;
      state.registration.loading = payload;
    },
    registrationSetError(state, action: PayloadAction<string | undefined>) {
      const { payload } = action;
      state.registration.error = payload;
    },
    registrationReset(state) {
      state.registration = {
        step: 'otp',
        data: {},
        loading: false,
        error: undefined,
      };
    },
    sessionExpired(state) {
      state.regToken = undefined;
      state.maskedEmail = undefined;
      state.maskedPhone = undefined;
      state.twoFactorMethod = 'sms';
      state.twoFactorMaskedDestination = undefined;
      state.twoFactorExpiresInSeconds = undefined;
      state.isSubmitting = false;
      state.fieldErrors = {};
      state.status = 'idle';
      state.loginStep = 'credentials';
      state.error = 'Session Expired. Please Login Again.';
    },
  },
  extraReducers: (builder) => {
    builder
      // LOGIN
      .addCase(loginThunk.pending, (state) => {
        state.status = 'loading';
        state.isSubmitting = true;
        state.error = undefined;
        state.loginStep = 'credentials';
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.isSubmitting = false;
        state.regToken = action.payload.twoFactorToken;
        state.maskedEmail = action.payload.maskedEmail;
        state.maskedPhone = action.payload.maskedPhone;
        state.loginStep = 'choose2fa';
        state.error = undefined;
      })
      .addCase(loginThunk.rejected, (state, action) => {
        // Admin-forced credential reset: not a login failure. Seed the email the
        // recovery steps read and let Login route into the recovery flow.
        const payload: unknown = action.payload;
        if (
          typeof payload === 'object' &&
          payload !== null &&
          'credentialsResetRequired' in payload &&
          payload.credentialsResetRequired === true
        ) {
          const emailAddress =
            'emailAddress' in payload && typeof payload.emailAddress === 'string'
              ? payload.emailAddress
              : '';
          state.status = 'idle';
          state.isSubmitting = false;
          state.loginStep = 'credentials';
          state.error = undefined;
          state.fieldErrors = {};
          state.forgotPasswordEmailAddress = emailAddress;
          return;
        }

        state.status = 'error';
        state.isSubmitting = false;
        state.loginStep = 'credentials';

        const { fieldErrors, error } = getErrorsFromPayload(action.payload, action.error.message);

        state.fieldErrors = fieldErrors;
        state.error = error;
      })
      // LOGOUT
      .addCase(logoutThunk.fulfilled, () => ({
        ...initialAuthState,
      }))
      // VERIFY OTP (Registration step 1)
      .addCase(verifyOtpThunk.pending, (state) => {
        state.isSubmitting = true;
        state.fieldErrors = {};
      })
      .addCase(verifyOtpThunk.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.registrationStep = 'password';
        const {
          payload: { registrationToken },
        } = action;
        state.regToken = registrationToken;
      })
      .addCase(verifyOtpThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.fieldErrors =
          action.payload !== null &&
          typeof action.payload === 'object' &&
          'fieldErrors' in action.payload &&
          isFieldErrors(action.payload.fieldErrors)
            ? action.payload.fieldErrors
            : {};
      })
      // SET PASSWORD (step 2)
      .addCase(setPasswordThunk.pending, (state) => {
        state.isSubmitting = true;
        state.fieldErrors = {};
      })
      .addCase(setPasswordThunk.fulfilled, (state) => {
        state.isSubmitting = false;
        state.registrationStep = 'emailCode';
      })
      .addCase(setPasswordThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.fieldErrors =
          action.payload !== null &&
          typeof action.payload === 'object' &&
          'fieldErrors' in action.payload &&
          isFieldErrors(action.payload.fieldErrors)
            ? action.payload.fieldErrors
            : {};
      })
      // CONFIRM EMAIL (step 3)
      .addCase(confirmEmailThunk.pending, (state) => {
        state.isSubmitting = true;
        state.fieldErrors = {};
      })
      .addCase(confirmEmailThunk.fulfilled, (state) => {
        state.isSubmitting = false;
        state.registrationStep = 'phoneCode';
      })
      .addCase(confirmEmailThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.fieldErrors =
          action.payload !== null &&
          typeof action.payload === 'object' &&
          'fieldErrors' in action.payload &&
          isFieldErrors(action.payload.fieldErrors)
            ? action.payload.fieldErrors
            : {};
      })

      .addCase(sendEmailCodeThunk.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(sendEmailCodeThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(sendEmailCodeThunk.rejected, (state) => {
        state.status = 'error';
      })

      // RESEND EMAIL CODE
      .addCase(resendEmailCodeThunk.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(resendEmailCodeThunk.fulfilled, (state) => {
        state.isSubmitting = false;
      })
      .addCase(resendEmailCodeThunk.rejected, (state) => {
        state.isSubmitting = false;
      })
      // CONFIRM PHONE (step 4)
      .addCase(confirmPhoneThunk.pending, (state) => {
        state.isSubmitting = true;
        state.fieldErrors = {};
      })
      .addCase(confirmPhoneThunk.fulfilled, (state) => {
        state.isSubmitting = false;
        state.registrationStep = 'complete';
      })
      .addCase(confirmPhoneThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.fieldErrors =
          action.payload !== null &&
          typeof action.payload === 'object' &&
          'fieldErrors' in action.payload &&
          isFieldErrors(action.payload.fieldErrors)
            ? action.payload.fieldErrors
            : {};
      })
      // RESEND PHONE CODE
      .addCase(resendPhoneCodeThunk.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(resendPhoneCodeThunk.fulfilled, (state) => {
        state.isSubmitting = false;
      })
      .addCase(resendPhoneCodeThunk.rejected, (state) => {
        state.isSubmitting = false;
      })
      // SELECT 2FA
      .addCase(select2FAMethodThunk.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(select2FAMethodThunk.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.loginStep = 'verify2fa';
        const {
          meta: {
            arg: { method },
          },
        } = action;
        state.twoFactorMethod = method;
      })
      .addCase(select2FAMethodThunk.rejected, (state) => {
        state.isSubmitting = false;
      })
      .addCase(forgotPasswordThunk.pending, (state, action) => {
        state.status = 'loading';
        state.error = undefined;
        state.forgotPasswordEmailAddress = action.meta.arg.emailAddress;
        state.fieldErrors = {};
        state.isSubmitting = true;
      })
      .addCase(forgotPasswordThunk.fulfilled, (state) => {
        state.status = 'idle';
        state.isSubmitting = false;
      })
      .addCase(forgotPasswordThunk.rejected, (state, action) => {
        state.status = 'error';
        state.fieldErrors =
          action.payload !== null &&
          typeof action.payload === 'object' &&
          'fieldErrors' in action.payload &&
          isFieldErrors(action.payload.fieldErrors)
            ? action.payload.fieldErrors
            : {};
        state.error = action.payload?.message ?? action.error.message;
        state.isSubmitting = false;
      })

      .addCase(verifyRecoveryOtpThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
        state.isSubmitting = true;
      })
      .addCase(verifyRecoveryOtpThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.isSubmitting = false;
        state.recoveryToken = action.payload.recoveryToken;
        state.recoveryMaskedPhone = action.payload.maskedPhone;
      })
      .addCase(verifyRecoveryOtpThunk.rejected, (state, action) => {
        state.status = 'error';
        state.isSubmitting = false;
        state.fieldErrors =
          action.payload !== null &&
          typeof action.payload === 'object' &&
          'fieldErrors' in action.payload &&
          isFieldErrors(action.payload.fieldErrors)
            ? action.payload.fieldErrors
            : {};
        state.error = action.payload?.message ?? action.error.message;
      })
      .addCase(sendRecoveryTwoFactorThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(sendRecoveryTwoFactorThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(sendRecoveryTwoFactorThunk.rejected, (state, action) => {
        state.status = 'error';
        state.fieldErrors =
          action.payload !== null &&
          typeof action.payload === 'object' &&
          'fieldErrors' in action.payload &&
          isFieldErrors(action.payload.fieldErrors)
            ? action.payload.fieldErrors
            : {};
        state.error = action.payload?.message ?? action.error.message;
      })

      .addCase(verifyRecoveryTwoFactorThunk.pending, (state) => {
        state.status = 'loading';
        state.isSubmitting = true;
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(verifyRecoveryTwoFactorThunk.fulfilled, (state) => {
        state.status = 'idle';
        state.isSubmitting = false;
      })
      .addCase(verifyRecoveryTwoFactorThunk.rejected, (state, action) => {
        state.status = 'error';
        state.isSubmitting = false;
        state.fieldErrors =
          action.payload !== null &&
          typeof action.payload === 'object' &&
          'fieldErrors' in action.payload &&
          isFieldErrors(action.payload.fieldErrors)
            ? action.payload.fieldErrors
            : {};
        state.error = action.payload?.message ?? action.error.message;
      })
      // VERIFY 2FA
      .addCase(verify2faCodeThunk.pending, (state) => {
        state.isSubmitting = true;
        state.fieldErrors = {};
      })
      .addCase(verify2faCodeThunk.fulfilled, (state) => {
        state.isSubmitting = false;
        state.status = 'authenticated';
        state.loginStep = 'authenticated';
      })
      .addCase(verify2faCodeThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.fieldErrors = {
          code: action.payload?.message ?? action.error.message,
        };
      })
      // RESEND 2FA
      .addCase(resend2faCodeThunk.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(resend2faCodeThunk.fulfilled, (state) => {
        state.isSubmitting = false;
      })
      .addCase(resend2faCodeThunk.rejected, (state) => {
        state.isSubmitting = false;
      })
      .addCase(fetchAdminInfoThunk.pending, (state) => {
        state.isSubmitting = true;
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(fetchAdminInfoThunk.fulfilled, (state, action) => {
        state.isSubmitting = false;
        const { payload } = action;
        const name = payload.name;
        const email = payload.email;
        const companyName = payload.companyName;
        const phone = payload.phone;

        const prev = state.user ?? {
          id: payload.id,
          name: '',
          email: '',
          companyName: '',
          phone: '',
        };

        state.user = {
          ...prev,
          id: payload.id,
          name,
          email,
          companyName,
          phone,
          // keep any other fields you store on user (e.g., role)
          role: payload.role,
        };

        state.userId = payload.id;
      })
      .addCase(fetchAdminInfoThunk.rejected, (state, action) => {
        state.isSubmitting = false;

        state.status = 'error';
        state.isSubmitting = false;

        let fieldErrors: FieldErrors = {};
        if (
          action.payload !== undefined &&
          typeof action.payload === 'object' &&
          'fieldErrors' in action.payload &&
          isFieldErrors(action.payload.fieldErrors)
        ) {
          const {
            payload: { fieldErrors: extractedFieldErrors },
          } = action;
          fieldErrors = extractedFieldErrors;
        }

        state.fieldErrors = fieldErrors;
      });
  },
});

export const {
  actions: {
    clearLoginError,
    clearFieldErrors,
    setLoginStep,
    registrationNextStep,
    registrationPrevStep,
    registrationSetStep,
    registrationSetData,
    registrationSetLoading,
    registrationSetError,
    registrationReset,
    sessionExpired,
  },
} = authSlice;
export default authSlice.reducer;
